import { useState } from 'react'
import { Badge, Card, DataState, PageHeader } from '@/components/Common'
import { Field, FormSection, TextField } from '@/components/Form'
import { ConfirmDialog, Modal } from '@/components/Modal'
import { useAuth } from '@/lib/auth'
import { api, ApiError, type FacilityTree, type ItemList, type Reference, type WorklistItem } from '@/lib/api'
import { useFetch } from '@/lib/hooks'
import { formatPersonName } from '@/lib/format'

/**
 * The worklist data itself — scheduled procedure steps.
 *
 * *** THIS PAGE SHOWS PHI. *** Patient names, MRNs, birth dates and what
 * people are booked in for.
 *
 * Writes go through the same stored procedures the HL7 feed calls, so a step
 * added here is held to exactly the validation a feed message is. The console
 * cannot create a row the feed could not — if it could, the validation would be
 * a suggestion rather than a rule.
 */
export function Orders() {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [facility, setFacility] = useState('')
  const [modality, setModality] = useState('')
  const [patientId, setPatientId] = useState('')
  const [accession, setAccession] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)

  const query = new URLSearchParams()
  if (dateFrom) query.set('date_from', dateFrom.replace(/-/g, ''))
  if (dateTo) query.set('date_to', dateTo.replace(/-/g, ''))
  if (facility) query.set('facility_code', facility)
  if (modality) query.set('modality', modality)
  if (patientId) query.set('patient_id', patientId)
  if (accession) query.set('accession_number', accession)
  if (includeInactive) query.set('include_inactive', 'true')

  const items = useFetch<ItemList>(`/api/v1/items?${query.toString()}`)
  const tenancy = useFetch<FacilityTree[]>('/api/v1/tenancy')
  const reference = useFetch<Reference>('/api/v1/system/reference')
  const { can } = useAuth()

  const [adding, setAdding] = useState(false)
  const [cancelling, setCancelling] = useState<WorklistItem | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mayEdit = can('worklist_admin')

  const act = async (work: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await work()
      items.reload()
      return true
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err))
      return false
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title="Scheduled steps"
        subtitle="The worklist data. One row per scheduled procedure step."
        actions={
          mayEdit && (
            <button className="btn btn-primary" onClick={() => setAdding(true)}>
              Add step
            </button>
          )
        }
      />

      {error && <div className="notice notice-error">{error}</div>}

      <Card title="Find">
        <div className="filter-grid">
          <Field label="From">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </Field>
          <Field label="Facility">
            <select value={facility} onChange={(e) => setFacility(e.target.value)}>
              <option value="">any</option>
              {(tenancy.data ?? []).map((f) => (
                <option key={f.id} value={f.code}>
                  {f.code}
                </option>
              ))}
            </select>
          </Field>
          <TextField label="Modality" value={modality} onChange={setModality} maxLength={16} />
          <TextField label="Patient ID" value={patientId} onChange={setPatientId} />
          <TextField label="Accession" value={accession} onChange={setAccession} />
          <label className="check">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <span className="check-label">Include cancelled</span>
          </label>
        </div>
        <p className="hint">
          With no dates the window is today ± 7 days. Every search is bounded —
          this table is the one that grows without limit.
        </p>
      </Card>

      <Card>
        <DataState
          loading={items.loading}
          error={items.error}
          empty={(items.data?.items ?? []).length === 0}
          emptyMessage="Nothing scheduled in this window."
        />

        {(items.data?.items ?? []).length > 0 && (
          <div className="table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Patient</th>
                  <th>Accession</th>
                  <th>Procedure</th>
                  <th>Modality</th>
                  <th>Station</th>
                  <th>Site</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(items.data?.items ?? []).map((item) => (
                  <tr key={item.id} className={item.active ? undefined : 'row-deleted'}>
                    <td className="mono">
                      {formatDa(item.scheduled_procedure_step_start_date)}
                      <div className="hint">
                        {formatTm(item.scheduled_procedure_step_start_time)}
                      </div>
                    </td>
                    <td>
                      {formatPersonName(item.patient_name)}
                      <div className="hint mono">{item.patient_id}</div>
                    </td>
                    <td className="mono">{item.accession_number || '—'}</td>
                    <td>
                      {item.requested_procedure_description || '—'}
                      {!item.study_instance_uid && (
                        <div className="hint">no Study UID — the modality will invent one</div>
                      )}
                    </td>
                    <td>{item.modality}</td>
                    <td className="mono">{item.scheduled_station_ae_title || 'any'}</td>
                    <td>
                      {item.facility_code || <span className="warn-text">none</span>}
                      {item.department_code && (
                        <div className="hint">{item.department_code}</div>
                      )}
                    </td>
                    <td>
                      <Badge state={item.scheduled_procedure_step_status} />
                      <div className="hint">{item.origin}</div>
                    </td>
                    <td className="actions">
                      {mayEdit && item.active && (
                        <>
                          <select
                            defaultValue=""
                            disabled={busy}
                            onChange={(e) => {
                              const status = e.target.value
                              e.target.value = ''
                              if (status) {
                                void act(() =>
                                  api.post(`/api/v1/items/${item.id}/status`, { status }),
                                )
                              }
                            }}
                          >
                            <option value="">set status…</option>
                            {(reference.data?.sps_statuses ?? []).map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                          <button
                            className="btn btn-small btn-danger-quiet"
                            onClick={() => setCancelling(item)}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {items.data?.truncated && (
          <div className="notice notice-warn">
            Showing the first {items.data.limit}. Narrow the dates or the filters
            — this is a partial answer, not the whole schedule.
          </div>
        )}

        <p className="table-footnote">
          A step marked COMPLETED or DISCONTINUED drops off every worklist,
          because the default status filter excludes both.{' '}
          <strong>There is no MPPS yet</strong>, so nothing else does that: if
          the order feed never sends a completion, every step stays on the list
          and a technologist eventually scans somebody twice.
        </p>
      </Card>

      {adding && (
        <AddStep
          tenancy={tenancy.data ?? []}
          busy={busy}
          error={error}
          onClose={() => {
            setAdding(false)
            setError(null)
          }}
          onSave={async (draft) => {
            if (await act(() => api.post('/api/v1/items', draft))) setAdding(false)
          }}
        />
      )}

      {cancelling && (
        <ConfirmDialog
          title="Cancel this scheduled step?"
          confirmLabel="Cancel step"
          destructive
          busy={busy}
          error={error}
          onCancel={() => setCancelling(null)}
          onConfirm={async () => {
            if (
              await act(() =>
                api.post(`/api/v1/items/${cancelling.id}/cancel`, {
                  reason: 'Cancelled from the console',
                }),
              )
            ) {
              setCancelling(null)
            }
          }}
        >
          <p>
            {formatPersonName(cancelling.patient_name)} —{' '}
            {cancelling.requested_procedure_description || cancelling.modality} on{' '}
            {formatDa(cancelling.scheduled_procedure_step_start_date)}.
          </p>
          <p className="hint">
            The row is kept with the reason, not deleted. Somebody will
            eventually ask why a study was expected and never arrived, and a
            deleted row cannot answer.
          </p>
        </ConfirmDialog>
      )}
    </>
  )
}

function formatDa(da: string | null): string {
  if (!da || da.length !== 8) return da ?? '—'
  return `${da.slice(0, 4)}-${da.slice(4, 6)}-${da.slice(6, 8)}`
}

function formatTm(tm: string | null): string {
  if (!tm || tm.length < 4) return tm ?? ''
  return `${tm.slice(0, 2)}:${tm.slice(2, 4)}`
}

function AddStep({
  tenancy,
  busy,
  error,
  onClose,
  onSave,
}: {
  tenancy: FacilityTree[]
  busy: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: Record<string, unknown>) => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [draft, setDraft] = useState({
    external_id: '',
    facility_code: '',
    department_code: '',
    patient_id: '',
    patient_name: '',
    patient_birth_date: '',
    patient_sex: '',
    accession_number: '',
    requested_procedure_description: '',
    modality: '',
    scheduled_start_date: today,
    scheduled_start_time: '',
    scheduled_station_ae_title: '',
  })

  const set = (key: keyof typeof draft, value: string) =>
    setDraft((d) => ({ ...d, [key]: value }))

  const departments =
    tenancy.find((f) => f.code === draft.facility_code)?.departments ?? []
  const multiSite = tenancy.length > 1

  return (
    <Modal
      title="Add a scheduled step"
      subtitle="Goes through the same procedure the order feed calls."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={
              busy ||
              !draft.external_id ||
              !draft.patient_id ||
              !draft.patient_name ||
              !draft.modality
            }
            onClick={() =>
              onSave({
                ...draft,
                origin: 'console',
                scheduled_start_date: draft.scheduled_start_date.replace(/-/g, ''),
                scheduled_start_time: draft.scheduled_start_time.replace(/:/g, '') || null,
                facility_code: draft.facility_code || null,
                department_code: draft.department_code || null,
                patient_birth_date: draft.patient_birth_date.replace(/-/g, '') || null,
                patient_sex: draft.patient_sex || null,
                accession_number: draft.accession_number || null,
                requested_procedure_description: draft.requested_procedure_description || null,
                scheduled_station_ae_title: draft.scheduled_station_ae_title || null,
              })
            }
          >
            {busy ? 'Saving…' : 'Add step'}
          </button>
        </>
      }
    >
      {error && <div className="notice notice-error">{error}</div>}

      <FormSection
        title="Identity"
        description="One step per row. An order for a chest AND an abdomen is two entries with two different reference numbers."
      >
        <TextField
          label="Reference"
          value={draft.external_id}
          onChange={(v) => set('external_id', v)}
          required
          hint="Unique for this step. Adding the same reference again updates the existing entry rather than duplicating it."
        />
      </FormSection>

      <FormSection title="Patient">
        <div className="form-grid">
          <TextField
            label="Patient ID"
            value={draft.patient_id}
            onChange={(v) => set('patient_id', v)}
            required
          />
          <TextField
            label="Patient name"
            value={draft.patient_name}
            onChange={(v) => set('patient_name', v)}
            required
            placeholder="SMITH^JOHN^A"
            hint="DICOM form: Family^Given^Middle. Carets, not commas."
          />
          <Field label="Birth date">
            <input
              type="date"
              value={draft.patient_birth_date}
              onChange={(e) => set('patient_birth_date', e.target.value)}
            />
          </Field>
          <Field label="Sex">
            <select value={draft.patient_sex} onChange={(e) => set('patient_sex', e.target.value)}>
              <option value="">unknown</option>
              <option value="M">M</option>
              <option value="F">F</option>
              <option value="O">O</option>
            </select>
          </Field>
        </div>
      </FormSection>

      <FormSection title="Procedure">
        <div className="form-grid">
          <TextField
            label="Modality"
            value={draft.modality}
            onChange={(v) => set('modality', v.toUpperCase())}
            required
            maxLength={16}
            placeholder="CT"
          />
          <TextField
            label="Accession number"
            value={draft.accession_number}
            onChange={(v) => set('accession_number', v)}
            maxLength={16}
            hint="How a technologist finds a walk-in at the scanner. Worth filling in."
          />
          <TextField
            label="Description"
            value={draft.requested_procedure_description}
            onChange={(v) => set('requested_procedure_description', v)}
            maxLength={64}
          />
          <TextField
            label="Scheduled station AE title"
            value={draft.scheduled_station_ae_title}
            onChange={(v) => set('scheduled_station_ae_title', v)}
            maxLength={16}
            hint="Which device this is booked for. Leave blank for any."
          />
          <Field label="Date" hint="Required — a step with no date is invisible to every query.">
            <input
              type="date"
              value={draft.scheduled_start_date}
              onChange={(e) => set('scheduled_start_date', e.target.value)}
            />
          </Field>
          <Field label="Time">
            <input
              type="time"
              value={draft.scheduled_start_time}
              onChange={(e) => set('scheduled_start_time', e.target.value)}
            />
          </Field>
        </div>
      </FormSection>

      <FormSection
        title="Site"
        description={
          multiSite
            ? 'Required. A step with no facility is invisible to every site-scoped worklist.'
            : 'Optional while there is one facility.'
        }
      >
        <div className="form-grid">
          <Field label="Facility">
            <select
              value={draft.facility_code}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  facility_code: e.target.value,
                  department_code: '',
                }))
              }
            >
              <option value="">not set</option>
              {tenancy.map((f) => (
                <option key={f.id} value={f.code}>
                  {f.code} — {f.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Department">
            <select
              value={draft.department_code}
              disabled={!draft.facility_code}
              onChange={(e) => set('department_code', e.target.value)}
            >
              <option value="">not set</option>
              {departments.map((d) => (
                <option key={d.id} value={d.code}>
                  {d.code} — {d.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </FormSection>
    </Modal>
  )
}
